import { RouteProp, useRoute } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue, useSetAtom } from "jotai";
import { useEffect, useMemo, useRef } from "react";
import { useFragment } from "react-relay";

import { deriveIsRequest } from "@/features/BookingWizard/utils/deriveIsRequest";
import { useBookingPaymentCards } from "@/features/Square/hooks/useBookingPaymentCards/useBookingPaymentCards";
import { LessonsStackNavigatorParamList } from "@/navigators/LessonsStackNavigator/types";
import { useTrackEvent } from "@/utils/Analytics/Mixpanel/hooks/useTrackEvent";

import { initializeBookingWizardAtom } from "../../atoms/actions/initializeBookingWizard/initializeBookingWizardAtom";
import { BookingWizardDataAtom } from "../../atoms/stores";
import { useCompleteBooking } from "../../components/WizardFooter/hooks/useCompleteBooking/useCompleteBooking";
import { BookingWizard } from "../../types";
import { useGetRequiredWizardSteps } from "../useInitializeBookingWizard/hooks/useGetRequiredWizardSteps";
import { useInitializeLessonBookingWizardLessonType$key } from "./__generated__/useInitializeLessonBookingWizardLessonType.graphql";
import { useInitializeLessonBookingWizardNode$key } from "./__generated__/useInitializeLessonBookingWizardNode.graphql";
import { useInitializeLessonBookingWizardSession$key } from "./__generated__/useInitializeLessonBookingWizardSession.graphql";

type LessonBookingProps = {
  sessionRef: useInitializeLessonBookingWizardSession$key | null;
  nodeRef: useInitializeLessonBookingWizardNode$key | null;
  lessonRatesRef: useInitializeLessonBookingWizardLessonType$key | null;
  slots: Array<{
    date: string;
    durationMins: number;
    id: string;
    time: string;
  }>;
  lessonTypeId: string;
  onClose: () => void;
  requestedTime?: string;
  durationMins?: number;
};

export const useInitializeLessonBookingWizard = ({
  sessionRef,
  nodeRef,
  lessonRatesRef,
  slots,
  lessonTypeId,
  onClose,
  requestedTime,
  durationMins: durationMinsProp,
}: LessonBookingProps) => {
  /**
   * Gather Data / atom setters
   */
  const session = useFragment(
    useInitializeLessonBookingWizardSessionFragmentString,
    sessionRef
  );
  const node = useFragment(
    useInitializeLessonBookingWizardNodeFragmentString,
    nodeRef
  );
  const lessonRates = useFragment(
    useInitializeLessonBookingWizardLessonTypeFragmentString,
    lessonRatesRef
  );

  const [handleCompleteBooking, isCompletingBooking] =
    useCompleteBooking(onClose);

  const initializeBookingWizard = useSetAtom(initializeBookingWizardAtom);
  const trackEvent = useTrackEvent();

  const bookingWizardData = useAtomValue(BookingWizardDataAtom);

  // Track whether auto-completion has already been triggered
  const hasAutoCompletedRef = useRef(false);

  // For lesson bookings, the node should be an AvailabilitySlot
  // Extract the data we need
  const startSlot = useMemo(() => {
    return node?.firstStartSlot?.edges?.[0]?.node;
  }, [node]);

  const startSlotId = startSlot?.id;
  const queueSlotId = startSlot?.agenda?.queueSlot?.id;

  const defaultCourseId = startSlot?.course?.id;

  const isPrivate = startSlot?.agenda?.facility?.club?.supportsPrivate ?? false;

  const isLessonRequest = deriveIsRequest(startSlot?.agenda?.facility);

  // Get lesson-specific data from route
  const route =
    useRoute<RouteProp<LessonsStackNavigatorParamList, "InstructorDetail">>();
  const instructorId = route.params?.instructorId;

  /**
   * Try to get initial duration
   * Start with query param if one exists
   * Then fall back to default duration
   * Then fall back to first available duration
   */
  const availabilitySlotDurationMinsParam = route.params?.duration;
  const availabilitySlotDurationMins =
    durationMinsProp ??
    (typeof availabilitySlotDurationMinsParam === "string"
      ? Number.parseInt(availabilitySlotDurationMinsParam, 10)
      : undefined);
  const defaultLessonDurationMins = lessonRates?.defaultDurationMins;
  const allDurations = lessonRates?.durations;
  const rawInitialDurationMins =
    availabilitySlotDurationMins && availabilitySlotDurationMins > 0
      ? availabilitySlotDurationMins
      : defaultLessonDurationMins && defaultLessonDurationMins > 0
      ? defaultLessonDurationMins
      : null;

  const initialDurationMins = allDurations?.includes(rawInitialDurationMins)
    ? rawInitialDurationMins
    : allDurations?.find((item) => !!item);

  const cards = useBookingPaymentCards(session, startSlot?.agenda?.date);
  const defaultPaymentCardOnFileId = cards.find((card) => card.default)?.id;

  const shouldAddPaymentCardOnFile = !!defaultPaymentCardOnFileId;
  const paymentCardOnFileId = shouldAddPaymentCardOnFile
    ? defaultPaymentCardOnFileId
    : undefined;

  // Get capacity information for lessons
  const { capacity, usedCapacity } = startSlot ?? {
    capacity: 4,
    usedCapacity: 3,
  };

  const availableCapacity = capacity - usedCapacity;
  const groupSize = availableCapacity;

  const ownerClientId = session?.currentClientProfile?.member?.id;
  const defaultRateId = lessonRates?.rates?.edges?.[0]?.node?.id;
  const firstSlotTime = slots[0]?.time ?? null;
  const lastSlotTime = slots[slots.length - 1]?.time ?? null;

  // Create the player round
  const currentBookingPlayerRound = useMemo(
    () => ({
      index: 0,
      id: null,
      type: "Lesson" as const,
      rate: null,
      player: {
        id: session?.currentClientProfile?.member?.id ?? "",
        firstName: session?.currentClientProfile?.member?.firstName ?? "",
        lastName: session?.currentClientProfile?.member?.lastName ?? "",
        profileImagePublicId:
          session?.currentClientProfile?.member?.profileImage?.publicId ?? null,
        clientType: isPrivate ? ("member" as const) : ("customer" as const),
        membershipTypeId:
          session?.currentClientProfile?.member?.membershipType?.id ?? "",
      },
    }),
    [
      session?.currentClientProfile?.member?.firstName,
      session?.currentClientProfile?.member?.lastName,
      session?.currentClientProfile?.member?.id,
      session?.currentClientProfile?.member?.profileImage?.publicId,
      session?.currentClientProfile?.member?.membershipType?.id,
      isPrivate,
    ]
  );

  // Extract all lesson start slot IDs
  const allLessonStartSlotIds = useMemo(() => {
    return (
      node?.allStartSlodIds?.edges?.map((edge) => edge?.node?.id ?? "") ?? []
    );
  }, [node]);

  // Build the lesson booking data
  const initialLessonBookingData: BookingWizard.LessonBookingWizardData | null =
    useMemo(() => {
      if (
        !ownerClientId ||
        !startSlotId ||
        !instructorId ||
        !lessonTypeId ||
        !initialDurationMins
      ) {
        return null;
      }

      return {
        type: "LessonBooking",
        ownerClientId,
        startSlotId: isLessonRequest
          ? queueSlotId ?? null
          : startSlotId ?? null,
        courseId: defaultCourseId ?? null,
        groupSize,
        paymentCardOnFileId,
        durationMins: initialDurationMins,
        rounds: [currentBookingPlayerRound],
        bookingRateId: null,
        playerRateId: defaultRateId ?? null,
        lessonTypeId,
        instructorId,
        allLessonStartSlotIds,
        isPrivate,
        isLessonBooking: true,
        minTime: isLessonRequest
          ? firstSlotTime ?? requestedTime ?? null
          : null,
        maxTime: isLessonRequest ? lastSlotTime ?? requestedTime ?? null : null,
        requestedTime: requestedTime ?? null,
        isRequest: isLessonRequest,
      };
    }, [
      ownerClientId,
      startSlotId,
      instructorId,
      lessonTypeId,
      groupSize,
      paymentCardOnFileId,
      initialDurationMins,
      currentBookingPlayerRound,
      defaultRateId,
      allLessonStartSlotIds,
      queueSlotId,
      requestedTime,
      firstSlotTime,
      lastSlotTime,
      defaultCourseId,
      isPrivate,
      isLessonRequest,
    ]);

  // Get required wizard steps for lessons
  const requiredWizardSteps: BookingWizard.BookingWizardStepTitle[] =
    useGetRequiredWizardSteps({
      sessionRef: session?.currentClientProfile ?? null,
      groupSize,
      requirePlayerCounts: true, // Always require player counts for lessons
      isDurationFacility: true, // Lessons are always duration facilities
      isLesson: true,
      availableCapacity: groupSize,
      isPrivate,
      canSelectVenue: isPrivate
        ? startSlot?.agenda?.facility?.privateLessonsPermissionSet
            ?.patronSelectVenue ?? false
        : startSlot?.agenda?.facility?.publicLessonsPermissionSet
            ?.patronSelectVenue ?? false,
    });

  useEffect(() => {
    if (initialLessonBookingData) {
      trackEvent("BookingWizard.Initialize", {
        initialBookingWizardData: initialLessonBookingData,
        requiredSteps: requiredWizardSteps,
      });
      initializeBookingWizard({
        initialBookingWizardData: initialLessonBookingData,
        requiredWizardSteps,
      });
    }
  }, [
    initializeBookingWizard,
    initialLessonBookingData,
    requiredWizardSteps,
    trackEvent,
  ]);

  useEffect(() => {
    if (
      requiredWizardSteps.length === 0 &&
      bookingWizardData &&
      !hasAutoCompletedRef.current
    ) {
      hasAutoCompletedRef.current = true;
      handleCompleteBooking();
    }
  }, [requiredWizardSteps, bookingWizardData, handleCompleteBooking]);

  return { availableCapacity, isCompletingBooking };
};

// GraphQL fragments
export const useInitializeLessonBookingWizardSessionFragmentString = graphql`
  fragment useInitializeLessonBookingWizardSession on Session {
    ...useBookingPaymentCards_session @arguments(bookingDate: $currentDate)
    currentClientProfile {
      ...useGetRequiredWizardSteps
      member {
        membershipType {
          id
        }
        ... on Member {
          id
          fullName
          firstName
          lastName
          profileImage {
            # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
            publicId: renderingKey
          }
        }
        ... on Customer {
          id
          firstName
          lastName
          profileImage {
            # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
            publicId: renderingKey
          }
        }
      }
    }
  }
`;

export const useInitializeLessonBookingWizardNodeFragmentString = graphql`
  fragment useInitializeLessonBookingWizardNode on Node {
    ... on AvailabilitySlot {
      id
      firstStartSlot: startSlots(
        first: 1
        slotAvailability: {
          facilityId: $facilityId
          bookingType: LESSON
          lessonTypeId: $lessonTypeId
        }
      ) {
        edges {
          node {
            id
            capacity(bookingType: LESSON, lessonTypeId: $lessonTypeId)
            usedCapacity
            course {
              id
            }
            agenda {
              date
              queueSlot {
                id
              }
              facility {
                club {
                  supportsPrivate
                }
                privateLessonsPermissionSet {
                  patronSelectVenue
                  patronAccess
                }
                publicLessonsPermissionSet {
                  patronSelectVenue
                  patronAccess
                }
                id
                name
                publicLessonsPermissionSet {
                  requirePaymentCardOnFile
                  prePaymentRequirement
                }
                minMaxGroupSize(bookingType: LESSON) {
                  min
                  max
                }
              }
            }
          }
        }
      }
      allStartSlodIds: startSlots(
        slotAvailability: {
          facilityId: $facilityId
          bookingType: LESSON
          lessonTypeId: $lessonTypeId
        }
      ) {
        edges {
          node {
            id
          }
        }
      }
    }
  }
`;

export const useInitializeLessonBookingWizardLessonTypeFragmentString = graphql`
  fragment useInitializeLessonBookingWizardLessonType on Node {
    id
    ... on LessonType {
      defaultDurationMins
      durations(instructorId: $instructorId)
      rates(
        instructorIds: [$instructorId]
        membershipTypeId: $membershipTypeId
      ) {
        edges {
          node {
            id
          }
        }
      }
    }
  }
`;
